<template>
  <div class="setup-password-container">
    <div v-if="header" class="r-header">
      {{header}}
    </div>
    <div v-else class="r-header">
      Reset Password
    </div>
    <div class="input-container">
      <div class="sac__font-16">
        CREATE PASSWORD
      </div>
      <input
        id="password"
        v-model="password"
        :type="inputType"
        class="p-input"
        autocomplete="false"
        placeholder="8 characters, a capital letter, and a symbol"
        aria-label="Password"
      >
      <div class="pwd-visibility">
        <img v-if="!showPassword" class="eyeball-img" src="@shared/assets/img/general/show-password.png"
          @click="toggleVisibility()">
        <img v-else class="eyeball-img" src="@shared/assets/img/general/hide-password.png"
          @click="toggleVisibility()">
      </div>
      <div v-if="errorObj.passwordErr || passStrengthErr" class="password-error">
        {{ errorObj.passwordErr || passStrengthErr }}
      </div>
    </div>
    <div class="input-container">
      <div class="sac__font-16">
        CONFIRM PASSWORD
      </div>
      <input
        id="confirm"
        v-model="confirmPassword"
        :type="inputTypeConfirm"
        class="p-input"
        autocomplete="false"
        placeholder="8 characters, a capital letter, and a symbol"
        aria-label="Confirm Password"
      >
      <div class="pwd-visibility">
        <img v-if="!showPasswordConfirm" class="eyeball-img" src="@shared/assets/img/general/show-password.png"
          @click="toggleVisibilityConfirm()">
        <img v-else class="eyeball-img" src="@shared/assets/img/general/hide-password.png"
          @click="toggleVisibilityConfirm()">
      </div>
      <div v-if="errorObj.confirmPasswordErr" class="password-error">
        {{ errorObj.confirmPasswordErr }}
      </div>
    </div>
    <button
      type="button"
      class="create-btn"
      @click="verifyPassword()"
    >
      <span v-if="!processing">{{ buttonLabel }}</span>
      <img v-else src="@shared/assets/img/general/loading-white.svg" class="spinner" alt="Processing">
    </button>
    <div v-if="welcome">
      <div class="border-container">
        <div class="divider-line" />
        <div>OR</div>
        <div class="divider-line" />
      </div>
      <div class="auth-container">
        <button class="google-auth-btn" @click="signupWithGoogle">
          <img src="@shared/assets/img/logo/google.svg" class="google-log">
          <span class="sac__font-16 n70 med">Sign Up with Google</span>
        </button>
      </div>
    </div>
    <div v-if="error" class="backend-error">
      {{ error }}
    </div>
    <div v-else-if="success" class="success-msg">
      {{ success }}
    </div>
    <div class="logo-container">
      <img src="@shared/assets/img/logo/black-logo.png" class="logo" alt="SmartAC.com">
    </div>
  </div>
</template>

<script>
import errorCatch from '@shared/lib/errorCatch';
import {
  readAlphaDomainPool,
  postOAuthForm,
} from '@shared/lib/general/alphaAuth';

export default {
  name: 'SACSetPassword',
  props: {
    infoText: String,
    header: String,
    buttonLabel: String,
    processing: Boolean,
    error: String,
    success: String,
    createPassword: Function,
    welcome: Boolean,
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      errorObj: {},
      passStrengthErr: '',
      matchedReq: 0,
      showPassword: false,
      showConfirmPassword: false,
      inputType: 'password',
      inputTypeConfirm: 'password',
    };
  },
  methods: {
    passwordValidation(p) {
      let error;
      // eslint-disable-next-line
      const isSpecial = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
      const isUppercase = /[A-Z]/;
      const isLowercase = /[a-z]/;
      const isNumber = /[0-9]/;

      if (!p.length > 7) {
        error = 'Password must be at least 8 characters long.';
      }
      if (!isSpecial.test(p)) {
        error = 'Password must include one special character.';
      }
      if (!isLowercase.test(p)) {
        error = 'Password must include one lowercase character.';
      }
      if (!isUppercase.test(p)) {
        error = 'Password must include one uppercase character.';
      }
      if (!isNumber.test(p)) {
        error = 'Password must include one number character.';
      }
      return error;
    },
    verifyPassword() {
      const {
        password,
        confirmPassword,
      } = this;
      this.passStrengthErr = this.passwordValidation(password);
      if (!this.passStrengthErr) {
        this.errorObj = errorCatch('reset', { password, confirmPassword });
        if (Object.keys(this.errorObj).length === 0) {
          this.createPassword(password);
        }
      }
    },
    toggleVisibility() {
      this.showPassword = !this.showPassword;
      if (this.showPassword) {
        this.inputType = 'text';
      } else {
        this.inputType = 'password';
      }
    },
    toggleVisibilityConfirm() {
      this.showPasswordConfirm = !this.showPasswordConfirm;
      if (this.showPasswordConfirm) {
        this.inputTypeConfirm = 'text';
      } else {
        this.inputTypeConfirm = 'password';
      }
    },
    signupWithGoogle() {
      postOAuthForm({
        provider: 'Google',
        pool: readAlphaDomainPool(),
        redirectUri: `${window.location.origin}/login/verify`,
        state: `/create-password/${this.$route.params.token}`,
      });
    },
  },
};
</script>

<style lang="scss">
.setup-password-container {
  width: 400px;
  min-height: 40vh;
  display: grid;
  gap: 1.5rem;

  @media (max-width: 420px) {
    width: 300px;
    gap: 1rem;
  }

  .r-header {
    font-size: 2.5rem;
    color: $BLACK;
    margin-bottom: 5vh;
    text-align: center;
  }
  .logo-container {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      margin-top: 5rem;

      .logo {
        width: 184px;
        height: 21px;

        @media (max-width: 420px) {
          width: 120px;
          height: 15px;
        }

        @media (max-width: 320px) {
          width: 100px;
          height: 12px;
        }
      }
    }
  .input-container {
    position: relative;
    display: grid;
    gap: 8px;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    .p-input {
      border: 2px solid $N20;
      width: 100%;
      padding: 17px 16px;
      font-size: 14px;

      &:focus {
        background-color: $NBLUE10;
      }

      @media (max-width: 420px) {
        height: 35px;
      }
    }

    .pwd-visibility {
      position: absolute;
      right: 10px;
      top: -4px;

      @media (max-width: 420px) {
        top: 35px;
      }

      .eyeball-img {
        height: 20px;
        width: 20px;
        cursor: pointer;

        @media (max-width: 420px) {
          height: 15px;
          width: 15px;
        }
      }
    }

    .password-error {
      color: $TOMATO100;
      font-size: 0.875rem;
      font-weight: 500;
      margin-top: 5px;

      @media (max-width: 420px) {
        font-size: 0.75rem;
      }
    }

    input::-webkit-input-placeholder {
      color: $N40;
    }
  }

  .create-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 56px;
    background-color: $NBLUE100;
    border-radius: 6px;
    color: $WHITE;
    font-size: 1rem;
    font-weight: 600;
    text-transform: uppercase;
    margin-top: 8px;

    @media (max-width: 420px) {
      height: 35px;
    }
  }

  .backend-error {
    color: $TOMATO100;
    text-align: center;
    font-weight: 500;
    background-color: $TOMATO25;
    padding: 4px 0;
    border-radius: 4px;
  }

  .success-msg {
    color: $BASIL100;
    text-align: center;
    font-weight: 500;
    background-color: #edfff3;
    padding: 4px 0;
    border-radius: 4px;
  }
  .pwd-visibility {
    position: absolute;
    position: absolute;
    right: 160px;
    margin-top: 55px;

    @media (max-width: 420px) {
      right: 50px;

    }

    .eyeball-img {
      height: 20px;
      width: 20px;
      cursor: pointer;

      @media (max-width: 420px) {
        height: 15px;
        width: 15px;
      }
    }
  }
}
</style>
